import { http } from '@/api/http';
import { isAdminLocalModeEnabled, isPublicMockEnabled } from '@/config/featureFlags';
import type { DisplayOrderUpdateInput } from '@/features/board/adminOrdering';
import { withBoardReadFallback } from '@/features/board/requestFallback';
import {
  createMockHistoryEntry,
  deleteMockHistoryEntry,
  fetchMockHistoryEntries,
  fetchMockAdminHistoryEntries,
  fetchMockAdminHistoryEntry,
  updateMockHistoryEntryDisplayOrders,
  updateMockHistoryEntry,
} from '@/features/content/mockData';
import type {
  AdminHistoryEntryDetail,
  AdminHistoryEntrySummary,
  HistoryEntryMutationInput,
  HistoryEntryResponse,
} from '@/features/content/types';

const appendIfPresent = (formData: FormData, key: string, value: File | null) => {
  if (value) {
    formData.append(key, value);
  }
};

const createHistoryEntryFormData = (input: HistoryEntryMutationInput) => {
  const formData = new FormData();
  formData.append('year', String(input.year));
  formData.append('title', input.title);
  formData.append('description', input.description);
  formData.append('imageFit', input.imageFit);
  formData.append('imageObjectPosition', input.imageObjectPosition);
  formData.append('imageScale', String(input.imageScale));
  formData.append('isPublished', String(input.isPublished));
  formData.append('publishedAt', input.publishedAt);
  appendIfPresent(formData, 'image', input.image);
  return formData;
};

const shouldUsePublicContentMocks = isPublicMockEnabled();
const shouldUseAdminContentLocalMode = isAdminLocalModeEnabled();

export const fetchHistoryEntries = async (): Promise<HistoryEntryResponse[]> => {
  return withBoardReadFallback({
    apiRequest: () => http.get('/api/v1/history-entries'),
    fallbackRequest: fetchMockHistoryEntries,
    label: 'public history entries',
    useMock: shouldUsePublicContentMocks,
  });
};

export const fetchAdminHistoryEntries = async (): Promise<AdminHistoryEntrySummary[]> => {
  return withBoardReadFallback({
    apiRequest: () => http.get('/api/v1/admin/history-entries'),
    fallbackRequest: fetchMockAdminHistoryEntries,
    label: 'admin history entries',
    useMock: shouldUseAdminContentLocalMode,
  });
};

export const fetchAdminHistoryEntry = async (
  historyEntryId: number,
): Promise<AdminHistoryEntryDetail> => {
  return withBoardReadFallback({
    apiRequest: () => http.get(`/api/v1/admin/history-entries/${String(historyEntryId)}`),
    fallbackRequest: () => fetchMockAdminHistoryEntry(historyEntryId),
    label: `admin history entry:${String(historyEntryId)}`,
    useMock: shouldUseAdminContentLocalMode,
  });
};

export const createHistoryEntry = async (
  input: HistoryEntryMutationInput,
): Promise<AdminHistoryEntryDetail> => {
  if (shouldUseAdminContentLocalMode) {
    return createMockHistoryEntry(input);
  }

  return http.post('/api/v1/admin/history-entries', createHistoryEntryFormData(input));
};

export const updateHistoryEntry = async (
  historyEntryId: number,
  input: HistoryEntryMutationInput,
): Promise<AdminHistoryEntryDetail> => {
  if (shouldUseAdminContentLocalMode) {
    return updateMockHistoryEntry(historyEntryId, input);
  }

  return http.put(
    `/api/v1/admin/history-entries/${String(historyEntryId)}`,
    createHistoryEntryFormData(input),
  );
};

export const deleteHistoryEntry = async (historyEntryId: number): Promise<void> => {
  if (shouldUseAdminContentLocalMode) {
    return deleteMockHistoryEntry(historyEntryId);
  }

  return http.delete(`/api/v1/admin/history-entries/${String(historyEntryId)}`);
};

export const updateHistoryEntryDisplayOrders = async (
  items: DisplayOrderUpdateInput[],
): Promise<void> => {
  if (shouldUseAdminContentLocalMode) {
    return updateMockHistoryEntryDisplayOrders(items);
  }

  return http.patch('/api/v1/admin/history-entries/display-orders', { items });
};
